import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

class BbdRebuyCrosssells extends HTMLElement {
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, any>}
   */
  #cachedRecommendations = {};

  /**
   * Bound onCartUpdate function
   * @type {Function | null}
   */
  #onCartUpdateBound = null;

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#loadRecommendations();
    this.#mutationObserver.observe(this, { attributes: true });

    this.#onCartUpdateBound = this.#onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateBound);
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateBound);
  }

  /**
   * Trigger a reload when the cart is updated
   */
  #onCartUpdate() {
    this.#loadRecommendations();
  }

  /**
   * Build query string for cart[items] array
   * @param {any[]} items
   * @returns {string}
   */
  #buildCartItemsQueryString(items) {
    return items
      .map((item, index) => {
        const prefix = `cart[items][${index}]`;
        return `${encodeURIComponent(`${prefix}[product_id]`)}=${item.product_id}&${encodeURIComponent(
          `${prefix}[variant_id]`
        )}=${item.variant_id}&${encodeURIComponent(`${prefix}[quantity]`)}=${item.quantity}`;
      })
      .join('&');
  }

  /**
   * Load the recommendations from Rebuy
   */
  async #loadRecommendations() {
    const { rebuyId, apiKey, customerId } = this.dataset;

    if (!rebuyId || !apiKey) {
      this.#handleError(new Error('Rebuy Widget ID and API Key are required'));
      return;
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      // Fetch the current cart data
      const cartResponse = await fetch('/cart.js', { signal: controller.signal });
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) {
        this.#handleError(new Error('Cart is empty'));
        return;
      }

      // Try each product in the cart until we get recommendations
      for (const item of cart.items) {
        const productId = item.product_id;
        const variantId = item.variant_id;

        const params = new URLSearchParams({
          key: apiKey,
          shopify_product_ids: productId,
          shopify_variant_ids: variantId,
          cart_subtotal: cart.total_price,
          cart_line_count: cart.item_count,
          cart_item_count: cart.item_count,
          limit: '10'
        });

        if (customerId) {
          params.set('shopify_customer_id', customerId);
        }

        const url = `https://rebuyengine.com/api/v1/custom/id/${rebuyId}?${params.toString()}`;

        // Check cache first
        const cachedResponse = this.#cachedRecommendations[url];
        if (cachedResponse && cachedResponse.data?.length > 0) {
          this.#renderRecommendations(cachedResponse);
          return;
        }

        // Fetch from API
        const response = await fetch(url, { signal: controller.signal });

        if (response.ok) {
          const data = await response.json();
          // If we have products, cache and render
          if (data.data && data.data.length > 0) {
            this.#cachedRecommendations[url] = data;
            this.#renderRecommendations(data);
            return;
          }
        }

        // If we reach here, this specific product returned no results or error.
        // The loop continues to the next item in the cart.
      }

      // If the loop finishes without returning, no items produced recommendations
      this.#handleError(new Error('No recommendations returned for any product in the cart'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Render the recommendations data
   * @param {any} data
   */
  async #renderRecommendations(data) {
    const products = data.data || [];
    if (products.length === 0) {
      this.#handleError(new Error('No recommendations returned from Rebuy'));
      return;
    }

    const productIds = products.map((product) => product.id);
    const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');

    // We use the Section Renderer API with the predictive search endpoint
    // to fetch the section HTML with the product IDs
    const sectionId = this.dataset.sectionId;
    // eslint-disable-next-line
    const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(Math.min(productIds.length, 10)));

    try {
      const htmlText = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const newSection = doc.querySelector(`bbd-rebuy-crosssells[id="${this.id}"]`);

      if (newSection && newSection.innerHTML.trim().length) {
        this.innerHTML = newSection.innerHTML;
        this.dataset.recommendationsPerformed = 'true';
        const datalayerObserver = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0]?.isIntersecting) return;
            obs.disconnect();
            this.dispatchEvent(new CustomEvent('datalayer:list-ready', { bubbles: true, detail: { container: this } }));
          },
          { rootMargin: '0px 0px 200px 0px' }
        );
        datalayerObserver.observe(this);
      } else {
        this.#handleError(new Error('Failed to render recommendations HTML'));
      }
    } catch (error) {
      this.#handleError(error);
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('BBD Rebuy Cross Sells error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading Rebuy recommendations';
  }
}

if (!customElements.get('bbd-rebuy-crosssells')) {
  customElements.define('bbd-rebuy-crosssells', BbdRebuyCrosssells);
}
